/** Reads the server message from the standard `{ error: { message } }` envelope. */
export function getApiErrorPayloadMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;
  const nested = record.error;
  if (nested && typeof nested === 'object' && nested !== null) {
    const message = (nested as Record<string, unknown>).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  if (typeof record.message === 'string' && record.message.length > 0) return record.message;
  return undefined;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message?: string,
    public data?: any
  ) {
    super(message || statusText);
    this.name = 'ApiError';
  }
}

export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends Error {
  constructor(message: string = 'Request timeout') {
    super(message);
    this.name = 'TimeoutError';
  }
}

export function handleApiError(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return 'Authentication required. Please log in.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Resource not found.';
      case 409:
        return (
          getApiErrorPayloadMessage(error.data) || 'Conflict. The resource may have been modified.'
        );
      case 422:
        return (
          getApiErrorPayloadMessage(error.data) || 'Validation error. Please check your input.'
        );
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return getApiErrorPayloadMessage(error.data) || error.message || 'An error occurred';
    }
  }

  if (error instanceof NetworkError) {
    return 'Network error. Please check your connection.';
  }

  if (error instanceof TimeoutError) {
    return 'Request timeout. Please try again.';
  }

  if (error instanceof Error && error.name === 'ValidationError') {
    return 'Invalid response format. Please contact support.';
  }

  if (error instanceof Error) {
    return error.message || 'An unexpected error occurred';
  }

  return 'An unexpected error occurred';
}
